import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Send, Trash2, Calendar, Clock, Users, Shield, X, AlertCircle, Eye, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGET_LABELS = {
  all: 'Hamısı',
  role: 'Rol üzrə',
  groups: 'Qruplar',
  users: 'Fərdi şəxslər',
  admins: 'Adminlər',
};

const ROLE_OPTIONS = [
  { id: 'technician', label: 'Texniklər' },
  { id: 'd2d_agent', label: 'D2D Agentlər' },
  { id: 'operator', label: 'Operatorlar' },
];

function toLocalIso(dt) {
  // Convert local datetime-local string to ISO with TZ
  if (!dt) return '';
  const d = new Date(dt);
  return d.toISOString();
}

export default function BroadcastPanel({ user }) {
  const token = localStorage.getItem('token');
  const isSuper = user?.role === 'superadmin' || user?.is_superadmin;

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('groups');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [scheduleNow, setScheduleNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [deliverPush, setDeliverPush] = useState(true);
  const [deliverInbox, setDeliverInbox] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipientData, setRecipientData] = useState(null);
  const [recipientFilter, setRecipientFilter] = useState('all'); // all/delivered/failed/seen/not_seen

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, groupsRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/groups`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/broadcast/sent`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setHistory(Array.isArray(histRes.data) ? histRes.data : []);
    } catch (e) {
      toast.error('Məlumatlar yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setTitle('');
    setBody('');
    setTargetType('groups');
    setSelectedRoles([]);
    setSelectedGroupIds([]);
    setSelectedUserIds([]);
    setScheduleNow(true);
    setScheduledAt('');
    setExpiresAt('');
    setDeliverPush(true);
    setDeliverInbox(true);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Başlıq və mətn vacibdir');
      return;
    }
    if (!expiresAt) {
      toast.error('Bitmə vaxtı (vaxt qədər göstərilsin) vacibdir');
      return;
    }

    if (!deliverPush && !deliverInbox) {
      toast.error('Ən azı bir çatdırılma kanalı seçin');
      return;
    }

    let target_ids = [];
    if (targetType === 'role') target_ids = selectedRoles;
    else if (targetType === 'groups') target_ids = selectedGroupIds;
    else if (targetType === 'users') target_ids = selectedUserIds;

    if (['role', 'groups', 'users'].includes(targetType) && target_ids.length === 0) {
      toast.error('Hədəfi seçin');
      return;
    }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      target_type: targetType,
      target_ids,
      expires_at: toLocalIso(expiresAt),
      scheduled_at: scheduleNow ? null : toLocalIso(scheduledAt),
      deliver_push: deliverPush,
      deliver_inbox: deliverInbox,
    };

    setSending(true);
    try {
      const res = await axios.post(`${API}/broadcast/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { recipient_count, scheduled, pushed_count } = res.data;
      if (scheduled) {
        toast.success(`Bildiriş planlaşdırıldı (${recipient_count} alıcı)`);
      } else {
        toast.success(`Göndərildi: ${pushed_count}/${recipient_count} cihaza çatdırıldı`);
      }
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Göndərmə uğursuz oldu');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Bu bildirişi silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/broadcast/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Silindi');
      fetchAll();
    } catch (e) {
      toast.error('Silmək mümkün olmadı');
    }
  };

  const openRecipients = async (id) => {
    setRecipientModalOpen(true);
    setRecipientData(null);
    setRecipientFilter('all');
    try {
      const res = await axios.get(`${API}/broadcast/${id}/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecipientData(res.data);
    } catch (e) {
      toast.error('Alıcı detalları yüklənmədi');
    }
  };

  const toggleArrayItem = (setter, arr, item) => {
    if (arr.includes(item)) setter(arr.filter(x => x !== item));
    else setter([...arr, item]);
  };

  // Filter users by current admin's groups
  const eligibleUsers = users.filter(u =>
    ['technician', 'd2d_agent', 'operator'].includes(u.role)
    || (isSuper && u.role === 'admin')
  );

  const eligibleGroups = groups;

  return (
    <div className="space-y-6" data-testid="broadcast-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-600" />
            Bildiriş Göndər
          </CardTitle>
          <p className="text-sm text-zinc-500">
            Mobil tətbiqdə işçilərə push bildiriş göndər. Bildiriş "Bitmə vaxtı"na qədər
            telefon profilində ayrıca bölmədə görünəcək.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">
              Başlıq <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Məs: Bu gün iclas saat 10:00"
              maxLength={80}
              data-testid="broadcast-title"
            />
            <p className="text-xs text-zinc-400 mt-1">{title.length}/80</p>
          </div>

          {/* Body */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">
              Mətn <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Bildirişin tam mətni..."
              rows={4}
              maxLength={500}
              data-testid="broadcast-body"
            />
            <p className="text-xs text-zinc-400 mt-1">{body.length}/500</p>
          </div>

          {/* Target type */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">
              Hədəf <span className="text-red-500">*</span>
            </label>
            <Select value={targetType} onValueChange={setTargetType}>
              <SelectTrigger data-testid="broadcast-target-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Hamısı (bütün işçilər)</SelectItem>
                <SelectItem value="role">Rol üzrə</SelectItem>
                <SelectItem value="groups">Qrup(lar)</SelectItem>
                <SelectItem value="users">Fərdi şəxslər</SelectItem>
                {isSuper && <SelectItem value="admins">Adminlər</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {/* Target details */}
          {targetType === 'role' && (
            <div className="border rounded-md p-3 space-y-2 bg-zinc-50">
              <p className="text-sm font-medium text-zinc-700">Rolları seç</p>
              {ROLE_OPTIONS.map(r => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedRoles.includes(r.id)}
                    onCheckedChange={() => toggleArrayItem(setSelectedRoles, selectedRoles, r.id)}
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          )}

          {targetType === 'groups' && (
            <div className="border rounded-md p-3 space-y-2 bg-zinc-50 max-h-60 overflow-y-auto">
              <p className="text-sm font-medium text-zinc-700">Qrupları seç</p>
              {eligibleGroups.length === 0 && <p className="text-xs text-zinc-400">Qrup tapılmadı</p>}
              {eligibleGroups.map(g => (
                <label key={g.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedGroupIds.includes(g.id)}
                    onCheckedChange={() => toggleArrayItem(setSelectedGroupIds, selectedGroupIds, g.id)}
                  />
                  <span className="text-sm">{g.name}</span>
                </label>
              ))}
            </div>
          )}

          {targetType === 'users' && (
            <div className="border rounded-md p-3 space-y-2 bg-zinc-50 max-h-60 overflow-y-auto">
              <p className="text-sm font-medium text-zinc-700">İstifadəçiləri seç ({selectedUserIds.length})</p>
              {eligibleUsers.length === 0 && <p className="text-xs text-zinc-400">İstifadəçi tapılmadı</p>}
              {eligibleUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedUserIds.includes(u.id)}
                    onCheckedChange={() => toggleArrayItem(setSelectedUserIds, selectedUserIds, u.id)}
                  />
                  <span className="text-sm">{u.full_name || u.username}</span>
                  <Badge variant="secondary" className="text-xs">{u.role}</Badge>
                </label>
              ))}
            </div>
          )}

          {targetType === 'admins' && (
            <div className="border rounded-md p-3 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Bütün adminlərə göndəriləcək
              </p>
            </div>
          )}

          {/* Delivery channels */}
          <div className="border rounded-md p-3 bg-zinc-50 space-y-2">
            <p className="text-sm font-medium text-zinc-700 mb-1">
              Çatdırılma kanalı <span className="text-red-500">*</span>
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={deliverPush}
                onCheckedChange={(v) => setDeliverPush(!!v)}
                data-testid="broadcast-deliver-push"
              />
              <span className="text-sm">📲 Push bildiriş (telefonda dərhal görsənir)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={deliverInbox}
                onCheckedChange={(v) => setDeliverInbox(!!v)}
                data-testid="broadcast-deliver-inbox"
              />
              <span className="text-sm">📋 Profildə qalan bildiriş (vaxt qədər görsənir)</span>
            </label>
            {!deliverPush && !deliverInbox && (
              <p className="text-xs text-red-600">Ən azı bir kanal seçilməlidir</p>
            )}
          </div>

          {/* Schedule */}
          <div className="border rounded-md p-3 space-y-3 bg-zinc-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={scheduleNow}
                onCheckedChange={(v) => setScheduleNow(!!v)}
                data-testid="broadcast-schedule-now"
              />
              <span className="text-sm font-medium">İndi göndər</span>
            </label>
            {!scheduleNow && (
              <div>
                <label className="text-sm text-zinc-700 mb-1 block">Göndərilmə vaxtı</label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  data-testid="broadcast-scheduled-at"
                />
              </div>
            )}
            <div>
              <label className="text-sm text-zinc-700 mb-1 block flex items-center gap-1">
                Bitmə vaxtı (orda görsənmə müddəti) <span className="text-red-500">*</span>
              </label>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="broadcast-expires-at"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Bu vaxta qədər istifadəçi profilində bildiriş kartı kimi göstəriləcək
              </p>
            </div>
          </div>

          <Button
            onClick={handleSend}
            disabled={sending}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="broadcast-send-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Göndərilir...' : (scheduleNow ? 'İndi Göndər' : 'Planlaşdır')}
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-zinc-700" />
            Tarixçə
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-zinc-500 text-center py-6">Yüklənir...</p>
          ) : history.length === 0 ? (
            <p className="text-zinc-400 text-center py-6 italic">Hələ heç bir bildiriş göndərilməyib</p>
          ) : (
            <div className="space-y-3">
              {history.map(h => {
                const now = new Date();
                const expires = new Date(h.expires_at);
                const scheduled = new Date(h.scheduled_at);
                const isActive = scheduled <= now && now < expires;
                const isExpired = expires <= now;
                const isPending = scheduled > now;
                return (
                  <div key={h.id} className="border rounded-md p-3 hover:bg-zinc-50" data-testid={`broadcast-item-${h.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-zinc-900">{h.title}</p>
                        <p className="text-sm text-zinc-600 line-clamp-2 mt-1">{h.body}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openRecipients(h.id)} className="text-blue-600 hover:bg-blue-50" data-testid={`view-recipients-${h.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCancel(h.id)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        <Users className="h-3 w-3 mr-1" />
                        {TARGET_LABELS[h.target_type] || h.target_type} · {h.recipient_count} alıcı
                      </Badge>
                      {isPending && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          <Calendar className="h-3 w-3 mr-1" />
                          Planlaşdırılıb: {scheduled.toLocaleString('az-AZ')}
                        </Badge>
                      )}
                      {isActive && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          ● Aktiv
                        </Badge>
                      )}
                      {isExpired && (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-500">
                          Bitib
                        </Badge>
                      )}
                      <span className="text-zinc-400 self-center">
                        Bitir: {expires.toLocaleString('az-AZ')}
                      </span>
                      {h.pushed_count != null && (
                        <span className="text-zinc-400 self-center">
                          · Göndərildi: {h.pushed_count}/{h.recipient_count}
                        </span>
                      )}
                      <Badge variant="outline" className="bg-zinc-50 text-zinc-600 text-xs">
                        {h.deliver_push !== false && '📲'}{h.deliver_inbox !== false && '📋'}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">
                      Göndərən: {h.sent_by_name || h.sent_by}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recipient Details Modal */}
      <Dialog open={recipientModalOpen} onOpenChange={setRecipientModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="recipient-modal">
          <DialogHeader>
            <DialogTitle>Alıcı Detalları</DialogTitle>
          </DialogHeader>
          {!recipientData ? (
            <p className="text-zinc-500 text-center py-6">Yüklənir...</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="font-medium text-zinc-900">{recipientData.title}</p>
                <p className="text-sm text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                  <span>Cəmi: {recipientData.total} alıcı</span>
                  {recipientData.deliver_push !== false && (
                    <>
                      <span className="text-green-600 font-medium">✓ Çatdı: {recipientData.delivered}</span>
                      <span className="text-red-600 font-medium">✗ Çatmadı: {recipientData.failed}</span>
                    </>
                  )}
                  {recipientData.inbox_only > 0 && (
                    <span className="text-zinc-600">📋 Yalnız Profil: {recipientData.inbox_only}</span>
                  )}
                  <span className="text-blue-600 font-medium">👁️ Gördü: {recipientData.seen || 0}</span>
                  <span className="text-amber-600 font-medium">⏳ Görmədi: {recipientData.not_seen || 0}</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-2 border-b">
                <button
                  onClick={() => setRecipientFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium ${recipientFilter === 'all' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-zinc-500'}`}
                >
                  Hamısı ({recipientData.total})
                </button>
                {recipientData.deliver_push !== false && (
                  <>
                    <button
                      onClick={() => setRecipientFilter('delivered')}
                      className={`px-3 py-1.5 text-sm font-medium ${recipientFilter === 'delivered' ? 'border-b-2 border-green-600 text-green-600' : 'text-zinc-500'}`}
                    >
                      ✓ Çatdı ({recipientData.delivered})
                    </button>
                    <button
                      onClick={() => setRecipientFilter('failed')}
                      className={`px-3 py-1.5 text-sm font-medium ${recipientFilter === 'failed' ? 'border-b-2 border-red-600 text-red-600' : 'text-zinc-500'}`}
                    >
                      ✗ Çatmadı ({recipientData.failed})
                    </button>
                  </>
                )}
                <button
                  onClick={() => setRecipientFilter('seen')}
                  className={`px-3 py-1.5 text-sm font-medium ${recipientFilter === 'seen' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-zinc-500'}`}
                >
                  👁️ Gördü ({recipientData.seen || 0})
                </button>
                <button
                  onClick={() => setRecipientFilter('not_seen')}
                  className={`px-3 py-1.5 text-sm font-medium ${recipientFilter === 'not_seen' ? 'border-b-2 border-amber-600 text-amber-600' : 'text-zinc-500'}`}
                >
                  ⏳ Görmədi ({recipientData.not_seen || 0})
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {recipientData.recipients
                  .filter(r => {
                    if (recipientFilter === 'all') return true;
                    if (recipientFilter === 'seen') return r.seen === true;
                    if (recipientFilter === 'not_seen') return r.seen !== true;
                    return r.status === recipientFilter;
                  })
                  .map(r => (
                    <div key={r.id} className="flex items-center justify-between p-2 hover:bg-zinc-50 rounded border">
                      <div className="flex items-center gap-2">
                        {r.status === 'delivered' && <CheckCircle className="h-4 w-4 text-green-600" />}
                        {r.status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                        {r.status === 'inbox_only' && <Bell className="h-4 w-4 text-zinc-500" />}
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            {r.user_name}
                            {r.seen === true ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 text-[10px]">👁️ Gördü</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px]">⏳ Görmədi</Badge>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            <Badge variant="secondary" className="text-xs">{r.user_role}</Badge>
                            {(r.group_names || []).map(g => (
                              <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                            ))}
                          </div>
                          {r.error && <p className="text-xs text-red-500 mt-1">{r.error}</p>}
                          {r.seen_at && (
                            <p className="text-[11px] text-blue-600 mt-1">
                              Gördü: {new Date(r.seen_at).toLocaleString('az-AZ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                {recipientData.recipients.filter(r => {
                  if (recipientFilter === 'all') return true;
                  if (recipientFilter === 'seen') return r.seen === true;
                  if (recipientFilter === 'not_seen') return r.seen !== true;
                  return r.status === recipientFilter;
                }).length === 0 && (
                  <p className="text-zinc-400 text-center py-6 italic">Bu kateqoriyada alıcı yoxdur</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
